import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { callBrandAuth } from '@/lib/brandApi';
import { sanitizeForDisplay } from '@/lib/sanitizeForDisplay';

export interface BrandUser {
  id: string;
  email: string;
}

export interface BrandAccount {
  id: string;
  company_name: string;
  company_logo_url: string | null;
  website_url: string | null;
  industry: string | null;
  contact_email: string;
  contact_phone: string | null;
  billing_address: Record<string, unknown> | null;
  credit_balance: number;
  is_verified: boolean;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

interface BrandAuthState {
  brandUser: BrandUser | null;
  brandAccount: BrandAccount | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface ValidateResponse {
  valid: boolean;
  brand_user?: BrandUser;
  brand_account?: BrandAccount;
  error?: string;
}

interface AuthResponse {
  brand_user: BrandUser;
  brand_account: BrandAccount;
  error?: string;
}

interface BrandAuthContextValue extends BrandAuthState {
  signup: (params: {
    email: string;
    password: string;
    company_name: string;
    website_url?: string;
    industry?: string;
    contact_phone?: string;
  }) => Promise<AuthResponse>;
  login: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const BrandAuthContext = createContext<BrandAuthContextValue | null>(null);

/**
 * Provider component that wraps brand portal pages.
 * Handles auth state and validation in one place to prevent multiple API calls.
 */
export function BrandAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BrandAuthState>({
    brandUser: null,
    brandAccount: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const validateToken = useCallback(async () => {
    // Call validate endpoint - the server reads the token from the httpOnly cookie
    const response = await callBrandAuth<ValidateResponse>('validate');

    if (response.error || !response.data?.valid) {
      setState({
        brandUser: null,
        brandAccount: null,
        isLoading: false,
        isAuthenticated: false,
      });
      return;
    }

    setState({
      brandUser: response.data.brand_user ?? null,
      brandAccount: response.data.brand_account ?? null,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const signup = useCallback(async (params: {
    email: string;
    password: string;
    company_name: string;
    website_url?: string;
    industry?: string;
    contact_phone?: string;
  }) => {
    const response = await callBrandAuth<AuthResponse>('signup', params);

    if (response.error) {
      throw new Error(sanitizeForDisplay(response.error));
    }

    if (response.data?.error) {
      throw new Error(sanitizeForDisplay(String(response.data.error)));
    }

    if (!response.data) {
      throw new Error('Signup failed');
    }

    // Cookie is automatically set by the server response
    setState({
      brandUser: response.data.brand_user,
      brandAccount: response.data.brand_account,
      isLoading: false,
      isAuthenticated: true,
    });

    return response.data;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await callBrandAuth<AuthResponse>('login', { email, password });

    if (response.error) {
      throw new Error(sanitizeForDisplay(response.error));
    }

    if (response.data?.error) {
      throw new Error(sanitizeForDisplay(String(response.data.error)));
    }

    if (!response.data) {
      throw new Error('Login failed');
    }

    // Cookie is automatically set by the server response
    setState({
      brandUser: response.data.brand_user,
      brandAccount: response.data.brand_account,
      isLoading: false,
      isAuthenticated: true,
    });

    return response.data;
  }, []);

  const logout = useCallback(async () => {
    // Call logout endpoint to clear the httpOnly cookie on the server
    await callBrandAuth('logout');
    
    setState({
      brandUser: null,
      brandAccount: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  const value: BrandAuthContextValue = {
    ...state,
    signup,
    login,
    logout,
    refreshAuth: validateToken,
  };

  return (
    <BrandAuthContext.Provider value={value}>
      {children}
    </BrandAuthContext.Provider>
  );
}

/**
 * Hook to access brand auth state and methods.
 * Must be used within a BrandAuthProvider.
 */
export function useBrandAuth() {
  const context = useContext(BrandAuthContext);
  
  if (!context) {
    throw new Error('useBrandAuth must be used within a BrandAuthProvider');
  }
  
  return context;
}
